import type { FormEvent } from 'react';
import React, { useState } from 'react';
import { range } from 'lodash-es';

import {
  AdornedInput,
  Adornment,
  DatePicker,
  DatePickerInput,
  Flex,
  FormField,
  FormHelp,
  Grid,
  IconCalendarOutline,
  IconPlusOutline,
  Input,
  Label,
  Paragraph,
  Spacer,
  Text,
  TextButton,
} from '@zillow/constellation';

import { AdditionalOccupantSection, Field } from '../../../models/index';
import { ADDITIONAL_OCCUPANT } from '../../../constants/index';
import { CenteredHR } from '../../Widgets/CenteredHR';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';
import { disableFutureDates, isYesNoEnabledForNumberedFields } from '../../../constants/functionConstants';
import { DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER } from '../../../constants/stringConstants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_ADDITIONAL_OCCUPANTS = 5;

type Props = {
  section: AdditionalOccupantSection;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: AdditionalOccupantSection) => void;
};

export default function AdditionalOccupant({ section, setSectionValues, setSection }: Props): JSX.Element {
  const [hasClickedNo, setHasClickedNo] = useState(false);

  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: AdditionalOccupantSection,
  });

  function onRemoveAll(): void {
    setHasClickedNo(true);
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function setFirstName(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.FIRST_NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setLastName(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.LAST_NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setAge(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setBirthdate(index: number, date: Date): void {
    const fieldName = Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, Number(index));

    setSectionValues({ [fieldName]: date });
  }

  function renderAdditionalOccupant(index: number): React.ReactNode {
    const firstNameField = section.getField(
      Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.FIRST_NAME, index),
    );
    const lastNameField = section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.LAST_NAME, index));
    const ageField = section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, index));
    const birthdateField = section.getField(
      Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, index),
    );

    return (
      <Spacer data-index={`occupant ${index}`} data-test="occupant" marginBottom="sm">
        {index > 0 && (
          <Flex display="flex" justifyContent="space-between" alignItems="center">
            <Text fontType="bodyHeading" id="additional-occupant">
              Additional occupant #{index + 1}
            </Text>
            <TextButton data-index={index} data-test="delete-occupant" onClick={onDeleteOne}>
              Delete
            </TextButton>
          </Flex>
        )}
        <Grid className={FULLSTORY_EXCLUDE} display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="xs">
          <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
            <FormField
              marginBottom="xs"
              label={<Label id="first-name">First name</Label>}
              control={
                <Input
                  data-index={index}
                  data-test="first-name"
                  value={firstNameField.enteredValue}
                  error={firstNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}
                  onChange={setFirstName}
                />
              }
              description={
                firstNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME) && (
                  <FormHelp error>{firstNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}</FormHelp>
                )
              }
            />
          </Grid>
          <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
            <FormField
              marginBottom="xs"
              label={<Label id="last-name">Last name</Label>}
              control={
                <Input
                  data-index={index}
                  data-test="last-name"
                  value={lastNameField.enteredValue}
                  error={lastNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}
                  onChange={setLastName}
                />
              }
              description={
                lastNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME) && (
                  <FormHelp error>{lastNameField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.NAME)}</FormHelp>
                )
              }
            />
          </Grid>
          {Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, index) &&
            section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.AGE, index)) && (
              <Grid gridColumnEnd="span 12">
                <FormField
                  label={<Label id="age">Age</Label>}
                  control={
                    <AdornedInput
                      inputId="AdornedInput-age"
                      rightAdornment={<Adornment>years old</Adornment>}
                      input={
                        <Input
                          aria-labelledby="additional occupant age"
                          data-index={index}
                          data-test="age"
                          onKeyDown={stopPropagationIfInvalidNumber}
                          type="number"
                          value={ageField.enteredValue || ''}
                          onChange={setAge}
                        />
                      }
                      error={ageField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.AGE)}
                    />
                  }
                  description={
                    ageField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.AGE) && (
                      <FormHelp error>{ageField.showOnError(ADDITIONAL_OCCUPANT.ERROR_MESSAGE.AGE)}</FormHelp>
                    )
                  }
                />
              </Grid>
            )}
          {Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, index) &&
            section.getField(Field.getIndexedFieldName(ADDITIONAL_OCCUPANT.FIELD_NAME.BIRTH_DATE, index)) && (
              <Grid gridColumnEnd="span 12">
                <FormField
                  label={<Label id="age">Birthdate</Label>}
                  control={
                    <AdornedInput
                      input={
                        <DatePickerInput
                          placeholder={DATE_PICKER_PLACEHOLDER}
                          value={birthdateField.enteredValue}
                          onChange={setBirthdate.bind(null, index)}
                          datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disableFutureDates} />}
                        />
                      }
                      rightAdornment={
                        <Adornment>
                          <IconCalendarOutline />
                        </Adornment>
                      }
                    />
                  }
                />
              </Grid>
            )}
        </Grid>
      </Spacer>
    );
  }

  const totalAdditionalOccupants = section.getTotalIndexes();
  const isEnabled = isYesNoEnabledForNumberedFields(section, 'additionalOccupant0_isEnabled', hasClickedNo);

  if (totalAdditionalOccupants == null) {
    return null;
  }

  return (
    <FormField
      label={
        <>
          <Label size="lg">Will there be any additional occupants?</Label>
          <Paragraph marginBottom="sm">
            Occupants should only be people who aren’t paying tenants such as children, elderly relatives, and other
            dependents.
          </Paragraph>
        </>
      }
      control={
        <YesAndNoExpansion yesHandler={onAddOne} noHandler={onRemoveAll} yesActive={isEnabled}>
          {range(0, totalAdditionalOccupants).map((index: number) => (
            <React.Fragment key={index}>
              {index > 0 && <CenteredHR />}
              {renderAdditionalOccupant(index)}
            </React.Fragment>
          ))}
          <Flex display="flex" justifyContent="center" marginBottom="sm">
            {totalAdditionalOccupants < MAXIMUM_ADDITIONAL_OCCUPANTS ? (
              totalAdditionalOccupants > 0 && (
                <TextButton
                  className="add-another"
                  icon={<IconPlusOutline />}
                  data-test="add-another"
                  onClick={onAddOne}
                >
                  Add another occupant
                </TextButton>
              )
            ) : (
              <Text data-test="maximum-reached">
                You’ve added {MAXIMUM_ADDITIONAL_OCCUPANTS} additional occupants, which is the maximum allowed.
              </Text>
            )}
          </Flex>
        </YesAndNoExpansion>
      }
    />
  );
}
